import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "../seo/SEO";
import {
  physicianSchema,
  medicalClinicSchemas,
  webPageSchema,
  breadcrumbSchema,
} from "../seo/schemas";

// Curated articles — link to long-form detail pages we've already written.
// Each entry: category, title, excerpt, date, readTime, to, image
const ARTICLES = [
  {
    category: "Liver",
    title: "Fatty Liver in Varanasi — what your reports actually mean",
    excerpt:
      "Most Indians today have a borderline-fatty liver. Here's how to read your ultrasound, what SGPT/SGOT really say, and when to act.",
    date: "Dec 2025",
    readTime: "6 min read",
    to: "/diseases/fatty-liver-treatment",
    image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=1200&q=80&auto=format&fit=crop",
  },
  {
    category: "Acidity & Reflux",
    title: "When is acidity NOT just acidity? Five red flags worth knowing.",
    excerpt:
      "Heartburn that wakes you up, weight loss with reflux, blood in vomit — these are the signs that need endoscopy, not antacids.",
    date: "Dec 2025",
    readTime: "5 min read",
    to: "/symptoms/acidity-treatment-varanasi",
    image: "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=1200&q=80&auto=format&fit=crop",
  },
  {
    category: "Hepatitis",
    title: "Hepatitis B — myths, vaccines and a one-tablet life.",
    excerpt:
      "Why HBV is silent for decades, how modern antivirals control it almost completely, and what 'cure' realistically means in 2025.",
    date: "Nov 2025",
    readTime: "7 min read",
    to: "/diseases/hepatitis-b-treatment",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&q=80&auto=format&fit=crop",
  },
  {
    category: "IBS & Bowel",
    title: "IBS is real — and the low-FODMAP plan that ends it.",
    excerpt:
      "If alternating diarrhoea and constipation has been part of your life for months, this is the structured plan that resolves it for most patients.",
    date: "Nov 2025",
    readTime: "8 min read",
    to: "/symptoms/ibs-symptoms-treatment",
    image: "https://images.unsplash.com/photo-1495214783159-3503fd1b572d?w=1200&q=80&auto=format&fit=crop",
  },
  {
    category: "Endoscopy",
    title: "Endoscopy in 15 minutes — what to actually expect.",
    excerpt:
      "From the consent room to the report — exactly what happens during an Upper GI Endoscopy under mild sedation, and why most patients underestimate how easy it is.",
    date: "Oct 2025",
    readTime: "5 min read",
    to: "/procedures/upper-gi-endoscopy",
    image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&q=80&auto=format&fit=crop",
  },
  {
    category: "Cancer Prevention",
    title: "Colonoscopy at 45 — the single most underrated screening test.",
    excerpt:
      "Why bowel cancer is now appearing earlier in India, who needs to screen, and how a 45-minute procedure can quietly save your life.",
    date: "Oct 2025",
    readTime: "6 min read",
    to: "/procedures/colonoscopy",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=80&auto=format&fit=crop",
  },
  {
    category: "Pancreas",
    title: "Pancreatitis — a survivable storm with the right care.",
    excerpt:
      "Acute pancreatitis is one of the most painful illnesses we treat. Here's what causes it, what an admission looks like, and how to never have a second attack.",
    date: "Sep 2025",
    readTime: "7 min read",
    to: "/diseases/pancreatitis-treatment",
    image: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=1200&q=80&auto=format&fit=crop",
  },
  {
    category: "IBD",
    title: "Ulcerative Colitis — life on (and beyond) mesalamine.",
    excerpt:
      "A long, honest look at what flares feel like, how modern biologics work, and what a 'good year' with UC actually looks like.",
    date: "Sep 2025",
    readTime: "8 min read",
    to: "/diseases/ulcerative-colitis-treatment",
    image: "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=1200&q=80&auto=format&fit=crop",
  },
];

const CATEGORIES = [...new Set(ARTICLES.map((a) => a.category))];

export default function Blog() {
  return (
    <main data-testid="blog-page">
      <SEO
        title="Blog — Gastroenterology & Liver Health by Dr. Indresh Dixit, Varanasi"
        description="Long-form, doctor-authored articles on gastroenterology, hepatology, endoscopy and patient health by Dr. Indresh Dixit, DM Gastroenterology (BHU), Varanasi."
        canonical="/blog"
        schemas={[
          physicianSchema(),
          ...medicalClinicSchemas(),
          webPageSchema({
            name: "Blog — Dr. Indresh Dixit",
            description: "Doctor-authored gastroenterology and liver health articles.",
            url: "/blog",
          }),
          breadcrumbSchema([
            { label: "Home", url: "/" },
            { label: "Blog" },
          ]),
        ]}
      />

      <section className="border-b border-black/10">
        <div className="container-x py-14 md:py-24">
          <div className="overline">Blog</div>
          <h1
            data-testid="blog-h1"
            className="font-serif-display mt-5 text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-tight max-w-4xl"
          >
            Doctor-authored articles on{" "}
            <span className="text-[#0370AD]">gastro &amp; liver health.</span>
          </h1>
          <p className="mt-7 text-black/70 max-w-2xl">
            Written by Dr. Indresh Dixit and team. Plain language, no
            sales-talk, no over-medicalisation. New articles added monthly.
          </p>
        </div>
      </section>

      {/* Featured article */}
      <section className="section-pad">
        <div className="container-x">
          <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight mb-10">
            Latest from the clinic.
          </h2>

          <Link
            to={ARTICLES[0].to}
            data-testid="blog-featured"
            className="group block bg-white border border-black/10 rounded-[6px] overflow-hidden hover:border-[#0370AD] transition-colors mb-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="aspect-[16/10] lg:aspect-auto overflow-hidden">
                <img
                  src={ARTICLES[0].image}
                  alt={ARTICLES[0].title}
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#0370AD] font-semibold">
                  Featured · {ARTICLES[0].category}
                </div>
                <h3 className="mt-4 font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight">
                  {ARTICLES[0].title}
                </h3>
                <p className="mt-5 text-black/70 max-w-xl">
                  {ARTICLES[0].excerpt}
                </p>
                <div className="mt-7 flex items-center gap-5">
                  <span className="text-[12px] tracking-[0.15em] uppercase text-black/55 font-semibold">
                    {ARTICLES[0].date} · {ARTICLES[0].readTime}
                  </span>
                  <span className="inline-flex items-center gap-2 font-semibold text-[14px] border-b border-black pb-0.5">
                    Read article
                    <ArrowUpRight className="h-4 w-4 arrow" />
                  </span>
                </div>
              </div>
            </div>
          </Link>

          {/* Category chips */}
          <div className="mb-10 flex flex-wrap gap-2.5">
            {CATEGORIES.map((c) => (
              <span
                key={c}
                className="px-4 py-2 rounded-full border border-black/15 text-[12.5px] font-medium"
              >
                {c}
              </span>
            ))}
          </div>

          {/* Grid of remaining articles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTICLES.slice(1).map((a, i) => (
              <Link
                key={a.to + i}
                to={a.to}
                data-testid={`blog-article-${i}`}
                className="group bg-white border border-black/10 rounded-[6px] overflow-hidden hover:border-[#0370AD] transition-colors flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[#0370AD] font-semibold">
                    {a.category}
                  </div>
                  <h3 className="mt-3 font-display-semibold text-[22px] leading-tight">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-black/65 flex-1">{a.excerpt}</p>
                  <div className="mt-5 pt-4 border-t border-black/10 flex items-center justify-between">
                    <span className="text-[11px] tracking-[0.15em] uppercase text-black/55 font-semibold">
                      {a.date} · {a.readTime}
                    </span>
                    <ArrowUpRight className="h-4 w-4 arrow text-[#0370AD]" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
